/**
 * Team Account Types
 *
 * Types for multi-user team management with seat-based access.
 * Note: Teams are for startups only. Investors are individual users.
 * Institutional investors (VC firms) would be a separate enterprise tier.
 */

import { getStartupFeatureLimit } from '@/lib/subscription/features';
import type { StartupTier } from '@/lib/subscription/types';

// ============================================================================
// Core Types
// ============================================================================

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: TeamRole;
  avatar?: string;
  status: TeamMemberStatus;
  invitedAt: string;
  joinedAt?: string;
  invitedBy: string;
  lastActiveAt?: string;
}

export type TeamRole = 'owner' | 'admin' | 'member';

export type TeamMemberStatus =
  | 'pending'    // Invited but not accepted
  | 'active'     // Active member
  | 'deactivated'; // Removed or deactivated

export interface TeamInvite {
  id: string;
  email: string;
  role: TeamRole;
  invitedBy: string;
  invitedAt: string;
  expiresAt: string;
  token: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
}

export interface Team {
  id: string;
  ownerId: string;
  name: string;
  createdAt: string;
  members: TeamMember[];
  pendingInvites: TeamInvite[];
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Base seat included in all plans (the owner)
 */
export const BASE_SEAT_COUNT = 1;

/**
 * Additional seats included in paid tiers
 */
export const ADDITIONAL_SEATS_BY_TIER = {
  free: 0,
  starter: 2,
  standard: 2,
  premium: 2,
} as const;

/**
 * Invite expiry duration in days
 */
export const INVITE_EXPIRY_DAYS = 7;

/**
 * Role display names
 */
export const ROLE_NAMES: Record<TeamRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

/**
 * Role descriptions
 */
export const ROLE_DESCRIPTIONS: Record<TeamRole, string> = {
  owner: 'Full access to all features and team management',
  admin: 'Can manage team members and access all features',
  member: 'Can access features but cannot manage team',
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Generate a unique invite token
 */
export function generateInviteToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

/**
 * Check if an invite has expired
 */
export function isInviteExpired(invite: TeamInvite): boolean {
  return new Date() > new Date(invite.expiresAt);
}

/**
 * Get the total seat count for a tier.
 *
 * B10: features.ts `team_seats` is the single source of truth for the paid
 * entitlement (free 1 / starter 2 / standard 4 / premium 6). The local
 * ADDITIONAL_SEATS_BY_TIER table had drifted to 1/3/3/3, blocking paying Premium
 * founders after 3 seats. Delegate to features.ts so the two can't diverge.
 */
export function getTotalSeats(tier: StartupTier): number {
  return getStartupFeatureLimit(tier, 'team_seats');
}

/**
 * Check if a role can manage team
 */
export function canManageTeam(role: TeamRole): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Check if a role can remove another role
 */
export function canRemoveRole(actorRole: TeamRole, targetRole: TeamRole): boolean {
  if (actorRole === 'owner') return targetRole !== 'owner';
  if (actorRole === 'admin') return targetRole === 'member';
  return false;
}

/**
 * Format member since date
 */
export function formatMemberSince(joinedAt: string): string {
  const date = new Date(joinedAt);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}
